import { useContext, useEffect, useRef, useState } from 'react';
import EditAppearance from '../../Modal/EditAppearance';
import EditLink, { domainOptions } from '../../Modal/EditLink';
import { ILink } from '../../../interfaces/Link';
import { UserContext } from '../../../context/UserContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Button } from '../Button';
import PreviewAppearance from '../../PreviewAppearance';
import PreviewLink from '../../PreviewLink';
import {
  useIsLinkAvailableQuery,
  useUpdateLinkMutation,
  useUpdateMetaTitleDescMutation,
  useUploadMetaImageMutation,
} from '../../../hooks/queries/useLinkQueries';
import IconButton from '../IconButton';
import { useNavigate } from 'react-router-dom';
import useI18n from '../../../hooks/useI18n';

interface LinkAndAppearanceEditCardProps {
  link: ILink;
  hasSubscription: boolean;
  handleModalClose: () => void;
  refetchLinks: () => Promise<void>;
}

export interface SelectedFile {
  name: string;
  size: number;
  preview: string;
  file: File;
}

export default function LinkAndAppearanceEditCard({
  link,
  hasSubscription,
  handleModalClose,
  refetchLinks,
}: LinkAndAppearanceEditCardProps) {
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useI18n();

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;
  const [activeTab, setActiveTab] = useState<'edit' | 'appearance'>('edit');
  const updateLinkMutation = useUpdateLinkMutation(user?._id ?? '');
  const updateMetaMutation = useUpdateMetaTitleDescMutation(user?._id ?? '');
  const uploadMetaImageMutation = useUploadMetaImageMutation(user?._id ?? '');

  // Formik for EditLink
  const editLinkFormik = useFormik({
    initialValues: {
      domain: link.domain || 'linkfluencer.io',
      subDomain: link.subDomain || '',
      shortUrl: link.shortUrl || '',
      tags: link.tags?.map((tag) => `#${tag}`).join(' ') || '',
    },
    validationSchema: Yup.object({
      shortUrl: Yup.string()
        .matches(
          /^[a-zA-Z0-9._-]+$/,
          'Only letters, numbers, dashes, underscores, and dots are allowed.',
        )
        .required('Required'),
      domain: Yup.string().test(
        'isPremium',
        'This domain is for premium users only. Please upgrade to select this domain.',
        function (value) {
          const domainOption = domainOptions.find(
            (option) => option.value === value,
          );
          if (domainOption && domainOption.premium && !hasSubscription) {
            return false;
          }
          return true;
        },
      ),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      if (!user) return;
      try {
        setSubmitting(true);
        const updatedLink: ILink = {
          ...link,
          domain: values.domain,
          subDomain: values.subDomain,
          shortUrl: values.shortUrl,
          tags: values.tags
            .split(' ')
            .map((tag) => tag.trim().replace(/^#/, ''))
            .filter((tag) => tag.length > 0),
        };
        await updateLinkMutation.mutateAsync(updatedLink);
        await refetchLinks();
        toast.success(t('links.updateSuccess'));
        handleModalClose();
      } catch (error) {
        toast.error(t('errors.failedToUpdateLink'));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  // Formik for EditAppearance
  const editAppearanceFormik = useFormik({
    initialValues: {
      title: link.metaTitle || '',
      description: link.metaDescription || '',
      file: null as File | null,
    },
    validationSchema: Yup.object({
      title: Yup.string().max(70, 'Maximum 70 characters allowed'),
      description: Yup.string().max(200, 'Maximum 200 characters allowed'),
    }),
    onSubmit: async (values) => {
      try {
        if (!user) return;
        await updateMetaMutation.mutateAsync({
          linkId: link._id,
          metaTitle: values.title,
          metaDescription: values.description,
        });
        await refetchLinks();
        toast.success(t('links.metaUpdateSuccess'));
        handleModalClose();
      } catch (error) {
        toast.error(t('errors.metaUpdateError'));
      }
    },
    enableReinitialize: true,
  });

  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(
    link.metaImageUrl || null,
  );

  const [isUploading, setIsUploading] = useState(false);
  const [hasFileChanged, setHasFileChanged] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: availabilityData } = useIsLinkAvailableQuery(
    user?._id ?? '',
    link._id,
    editLinkFormik.values.shortUrl,
    !editLinkFormik.errors.shortUrl && !!editLinkFormik.values.shortUrl,
  );
  const isAvailable = availabilityData?.isAvailable ?? null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size <= 5 * 1024 * 1024) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedFile({
            name: file.name,
            size: file.size,
            preview: reader.result as string,
            file: file,
          });
        };
        reader.readAsDataURL(file);
      } else {
        toast.error(String(t('messages.error.fileSizeLimit')));
      }
    }
  };

  const onUpgradeClick = () => {
    navigate('/plans');
  };

  const handleUpload = async () => {
    if (selectedFile?.file && user) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile.file);
      try {
        const response = await uploadMetaImageMutation.mutateAsync({
          linkId: link._id,
          formData,
        });
        setImageUrl(response.metaImageUrl);
        setHasFileChanged(true);
        await refetchLinks();
        toast.success(String(t('messages.success.imageUploaded')));
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            String(t('messages.error.imageUploadError')),
        );
      } finally {
        setSelectedFile(null);
        setIsUploading(false);
      }
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const hasNonDomainErrors = () => {
    const errors = { ...editLinkFormik.errors };
    delete errors.domain; // Remove domain error from consideration
    return Object.keys(errors).length > 0;
  };

  const shouldDisableSaveButton = () => {
    if (activeTab === 'edit') {
      // For edit tab
      const hasDomainError = Boolean(
        editLinkFormik.errors.domain &&
          editLinkFormik.values.domain !== 'linkfluencer.io',
      );

      return Boolean(
        hasNonDomainErrors() ||
          !editLinkFormik.dirty ||
          editLinkFormik.isSubmitting ||
          hasDomainError,
      );
    } else {
      // For appearance tab
      return Boolean(
        (!editAppearanceFormik.dirty && !hasFileChanged) ||
          editAppearanceFormik.isSubmitting ||
          Object.keys(editAppearanceFormik.errors).length > 0,
      );
    }
  };
  return (
    <div className="flex flex-col gap-8 w-full lg:w-[594px] max-h-[90vh] lg:max-h-[71.69vh]">
      <div className="border-b border-[#B3B3B2]">
        <div className="flex justify-between items-center">
          <div className="flex gap-4">
            <button
              className={`font-medium text-base py-[5px] px-2 hover:text-[#121111]
                ${
                  activeTab === 'edit'
                    ? 'border-b-2 border-black text-[#121111]'
                    : 'border-b-2 border-transparent text-[#4D4D4D]'
                }`}
              onClick={() => setActiveTab('edit')}
            >
              {t('links.editLink')}
            </button>
            <button
              className={`font-medium text-base py-[5px] px-2 hover:text-[#121111]
                 ${
                   activeTab === 'appearance'
                     ? 'border-b-2 border-black text-[#121111]'
                     : 'border-b-2 border-transparent text-[#4D4D4D]'
                 }`}
              onClick={() => setActiveTab('appearance')}
            >
              {t('links.editAppearance')}
            </button>
          </div>
          <IconButton
            src="/assets/svgs/fi_cross.svg"
            onClick={handleModalClose}
            className="size-50"
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        {/* Preview Section (Fixed at the top) */}
        <div className="mb-4">
          {activeTab === 'edit' ? (
            <PreviewLink
              domain={editLinkFormik.values.domain}
              subdomain={editLinkFormik.values.subDomain}
              linkKey={editLinkFormik.values.shortUrl}
            />
          ) : (
            <PreviewAppearance
              title={
                editAppearanceFormik.values.title &&
                editAppearanceFormik.values.title.trim() !== ''
                  ? editAppearanceFormik.values.title
                  : t('links.noTitleAvailable')
              }
              description={
                editAppearanceFormik.values.description &&
                editAppearanceFormik.values.description.trim() !== ''
                  ? editAppearanceFormik.values.description
                  : t('links.noDescriptionAvailable')
              }
              linkUrl={
                link.subDomain
                  ? `https://${link.subDomain}.${link.domain || 'linkfluencer.io'}/${link.shortUrl}`
                  : `https://${link.domain || 'linkfluencer.io'}/${link.shortUrl}`
              }
              imageUrl={imageUrl || ''}
            />
          )}
        </div>

        {/* Scrollable Edit Content */}
        <div className="overflow-y-auto hide-scrollbar max-h-[calc(90vh-200px)]">
          {activeTab === 'edit' ? (
            <EditLink
              isPremiumUser={hasSubscription}
              formik={editLinkFormik}
              isAvailable={isAvailable}
            />
          ) : (
            <EditAppearance
              fileInputRef={fileInputRef}
              formik={editAppearanceFormik}
              selectedFile={selectedFile}
              imageUrl={imageUrl}
              isUploading={isUploading}
              hasFileChanged={hasFileChanged}
              onFileSelect={handleFileSelect}
              onUpload={handleUpload}
              onImportClick={handleImportClick}
            />
          )}
        </div>
        {/* Save and Cancel Buttons (Fixed at the bottom) */}
        <div className="mt-4">
          <div className="flex gap-2 w-full flex-col-reverse lg:flex-row justify-end">
            <Button variant="outlined" onClick={handleModalClose}>
              {t('common.cancel')}
            </Button>
            {editLinkFormik.touched.domain &&
            editLinkFormik.errors.domain &&
            editLinkFormik.values.domain !== 'linkfluencer.io' ? (
              <Button onClick={onUpgradeClick}>{t('common.upgradeNow')}</Button>
            ) : (
              <Button
                type="submit"
                onClick={() =>
                  activeTab === 'edit'
                    ? editLinkFormik.handleSubmit()
                    : editAppearanceFormik.handleSubmit()
                }
                disabled={shouldDisableSaveButton()}
              >
                {activeTab === 'edit'
                  ? editLinkFormik.isSubmitting
                    ? t('common.saving')
                    : t('common.save')
                  : editAppearanceFormik.isSubmitting
                    ? t('common.saving')
                    : t('common.save')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
